import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  CircularProgress,
  Pagination,
  Button,
  useTheme,
  Drawer,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  AdminPanelSettings as AdminIcon,
  ChildCare as ReaderIcon,
  School as GradeIcon,
  Psychology as ConfidenceIcon,
  Bookmark as CategoryIcon,
  Schedule as GoalIcon,
  CheckCircle as CompletedIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { dashboardService } from '../../../services/dashboard-service';
import { toast } from 'react-toastify';

// High-fidelity fallback search results matching backend ReaderProfileEntity DTO
const mockReaderProfiles = [
  { id: "prof_fox_01", accountId: "acc_premium_01", name: "Tommy Shelby", gradeLevel: "KINDERGARTEN", readingConfidence: "VERY_LOW", avatarId: "avatar_fox", objectiveId: "obj_sight_words", readerId: "usr_reader_tommy", goal: { dailyMinutes: 10, weeklySessions: 5 }, interestedCategoryIds: ["cat_animals", "cat_adventure"], nativeLanguage: "ENGLISH", status: "ACTIVE", srpIntroStatus: "INTRODUCED", createdAt: "2026-02-15T09:00:00Z", createdBy: "usr_9281a", updatedAt: "2026-05-18T10:30:00Z", updatedBy: "usr_reader_tommy" },
  { id: "prof_unic_02", accountId: "acc_premium_01", name: "Grace Burgess", gradeLevel: "FOUR", readingConfidence: "HIGH", avatarId: "avatar_unicorn", objectiveId: "obj_fluent_read", readerId: "usr_reader_grace", goal: { dailyMinutes: 20, weeklySessions: 4 }, interestedCategoryIds: ["cat_fantasy", "cat_science"], nativeLanguage: "ENGLISH", status: "ACTIVE", srpIntroStatus: "INTRODUCED", createdAt: "2026-03-01T11:20:00Z", createdBy: "usr_9281a", updatedAt: null, updatedBy: null },
  { id: "prof_bear_03", accountId: "acc_trial_02", name: "Arthur Shelby", gradeLevel: "SEVEN", readingConfidence: "LOW", avatarId: "avatar_bear", objectiveId: "obj_phonics_blend", readerId: null, goal: { dailyMinutes: 15, weeklySessions: 3 }, interestedCategoryIds: ["cat_sports", "cat_history"], nativeLanguage: "SPANISH", status: "ACTIVE", srpIntroStatus: "NOT_INTRODUCED", createdAt: "2026-05-16T15:00:00Z", createdBy: "usr_3847b", updatedAt: "2026-05-17T09:00:00Z", updatedBy: "usr_system" },
  { id: "prof_owl_04", accountId: "acc_unlimited_03", name: "Polly Gray", gradeLevel: "ADULT", readingConfidence: "VERY_HIGH", avatarId: "avatar_owl", objectiveId: "obj_advanced_comprehension", readerId: "usr_reader_polly", goal: { dailyMinutes: 30, weeklySessions: 6 }, interestedCategoryIds: ["cat_mystery", "cat_biographies"], nativeLanguage: "FRENCH", status: "ACTIVE", srpIntroStatus: "INTRODUCED", createdAt: "2025-01-10T12:00:00Z", createdBy: "usr_root", updatedAt: "2026-02-22T14:00:00Z", updatedBy: "usr_platform_admin" },
  { id: "prof_lion_05", accountId: "acc_premium_04", name: "John Shelby", gradeLevel: "TWO", readingConfidence: "MEDIUM", avatarId: "avatar_lion", objectiveId: "obj_word_recognition", readerId: null, goal: { dailyMinutes: 15, weeklySessions: 5 }, interestedCategoryIds: ["cat_cartoons", "cat_animals"], nativeLanguage: "ENGLISH", status: "ACTIVE", srpIntroStatus: "NOT_INTRODUCED", createdAt: "2025-06-01T10:15:00Z", createdBy: "usr_5561f", updatedAt: null, updatedBy: null },
  { id: "prof_panda_06", accountId: "acc_premium_04", name: "Ada Thorne", gradeLevel: "FIVE", readingConfidence: "MEDIUM", avatarId: "avatar_panda", objectiveId: "obj_speed_reading", readerId: "usr_reader_ada", goal: { dailyMinutes: 20, weeklySessions: 5 }, interestedCategoryIds: ["cat_science", "cat_nature"], nativeLanguage: "ENGLISH", status: "ACTIVE", srpIntroStatus: "INTRODUCED", createdAt: "2025-07-15T08:45:00Z", createdBy: "usr_5561f", updatedAt: "2025-12-01T11:00:00Z", updatedBy: "usr_reader_ada" }
];

const categoryLabels = {
  "cat_animals": "Animals & Nature",
  "cat_adventure": "Adventure Stories",
  "cat_fantasy": "Fantasy & Magic",
  "cat_science": "Science & Tech",
  "cat_sports": "Sports & Games",
  "cat_history": "History & Culture",
  "cat_mystery": "Mystery & Detective",
  "cat_biographies": "Biographies",
  "cat_cartoons": "Comics & Cartoons",
  "cat_nature": "Earth & Environment"
};

const ReaderProfileManagementTab = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [objectives, setObjectives] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [isMocked, setIsMocked] = useState(false);

  // Search parameters state
  const [term, setTerm] = useState('');
  const [grade, setGrade] = useState('ALL');
  const [confidence, setConfidence] = useState('ALL');
  const [attached, setAttached] = useState('ALL');
  const [page, setPage] = useState(1);
  const size = 6; // Page size (3 columns grid makes 6 cards ideal)

  // Selected profile for drawer view
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const formatTimeSpent = (seconds) => {
    if (seconds === undefined || seconds === null) return '-';
    const minutes = Math.floor(seconds / 60);
    if (minutes === 0) return '0 mins';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins} mins`;
    if (mins === 0) return `${hrs} hrs`;
    return `${hrs} hrs ${mins} mins`;
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      
      const query = {
        term: term.trim() ? term.trim() : null,
        gradeLevel: grade === 'ALL' ? null : grade,
        readingConfidence: confidence === 'ALL' ? null : confidence,
        attached: attached === 'ALL' ? null : (attached === 'ATTACHED'),
        pageNumber: page - 1,
        pageSize: size
      };

      const result = await dashboardService.searchReaderProfiles(query);
      
      // Map ReaderProfileResult properties to UI friendly names if necessary
      const mappedProfiles = (result.items || []).map(p => ({
        ...p,
        id: p.profileId,
        goal: {
          dailyMinutes: p.goal?.dailyReadingGoalInMinutes || 0,
          weeklySessions: p.goal?.weekdays ? p.goal.weekdays.size || p.goal.weekdays.length : 0
        }
      }));

      setProfiles(mappedProfiles);
      setTotalCount(result.totalItems || 0);
      setIsMocked(false);
    } catch (err) {
      console.warn("Using offline mock data fallback for reader profile search query:", err.message);
      setIsMocked(true);
      
      let filtered = [...mockReaderProfiles];
      
      if (term.trim()) {
        const queryTerm = term.toLowerCase().trim();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(queryTerm) ||
          p.id.toLowerCase().includes(queryTerm) ||
          p.accountId.toLowerCase().includes(queryTerm)
        );
      }
      
      if (grade !== 'ALL') {
        filtered = filtered.filter(p => p.gradeLevel === grade);
      }
      
      if (confidence !== 'ALL') {
        filtered = filtered.filter(p => p.readingConfidence === confidence);
      }
      
      if (attached !== 'ALL') {
        const wantsAttached = attached === 'ATTACHED';
        filtered = filtered.filter(p => (p.readerId !== null) === wantsAttached);
      }

      const total = filtered.length;
      const start = (page - 1) * size;
      const paged = filtered.slice(start, start + size);
      
      setProfiles(paged);
      setTotalCount(total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [objs, avts] = await Promise.all([
          dashboardService.getReadingObjectives().catch(() => []),
          dashboardService.getAvatars().catch(() => [])
        ]);
        setObjectives(objs);
        setAvatars(avts);
      } catch (err) {
        console.warn("Failed to load platform metadata: ", err);
      }
    };
    loadMetadata();
  }, []);

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, grade, confidence, attached]);

  useEffect(() => {
    if (!selectedProfile) {
      setStats(null);
      return;
    }

    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const data = await dashboardService.getReaderStats(selectedProfile.id);
        setStats(data);
      } catch (err) {
        console.warn("Failed to fetch reader stats, using offline fallback:", err.message);
        
        // High-fidelity mock stats mapping based on profile ID:
        const mockStatsMap = {
          "prof_fox_01": { srpsCompleted: 18, totalTimeSeconds: 115 * 60, srpTimeSeconds: 85 * 60, btcTimeSeconds: 30 * 60 },
          "prof_unic_02": { srpsCompleted: 42, totalTimeSeconds: 310 * 60, srpTimeSeconds: 210 * 60, btcTimeSeconds: 100 * 60 },
          "prof_bear_03": { srpsCompleted: 8, totalTimeSeconds: 45 * 60, srpTimeSeconds: 30 * 60, btcTimeSeconds: 15 * 60 },
          "prof_owl_04": { srpsCompleted: 124, totalTimeSeconds: 950 * 60, srpTimeSeconds: 700 * 60, btcTimeSeconds: 250 * 60 },
          "prof_lion_05": { srpsCompleted: 0, totalTimeSeconds: 0, srpTimeSeconds: 0, btcTimeSeconds: 0 },
          "prof_panda_06": { srpsCompleted: 29, totalTimeSeconds: 210 * 60, srpTimeSeconds: 150 * 60, btcTimeSeconds: 60 * 60 }
        };

        const profileId = selectedProfile.id;
        if (mockStatsMap[profileId]) {
          setStats({ ...mockStatsMap[profileId], isMocked: true });
        } else {
          // Dynamic fallback for generated/random reader profile IDs
          const baseCount = (profileId || "").length * 3 || 12;
          const practices = baseCount;
          const seconds = practices * 8 * 60;
          setStats({
            srpsCompleted: practices,
            totalTimeSeconds: seconds,
            srpTimeSeconds: Math.floor(seconds * 0.7),
            btcTimeSeconds: Math.floor(seconds * 0.3),
            isMocked: true
          });
        }
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [selectedProfile]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProfiles();
  };

  const copyToClipboard = (text, typeName = "ID") => {
    navigator.clipboard.writeText(text);
    toast.info(`Copied ${typeName} to clipboard: ${text}`);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Avatar customization mappings
  const getAvatarConfig = (avatarId) => {
    // Check if the avatarId matches one of our active database avatars
    const dbAvatar = avatars.find(a => a.avatarId === avatarId);
    if (dbAvatar) {
      return {
        color: dbAvatar.backgroundColor || '#e8f0fe',
        border: dbAvatar.highlightColor || theme.palette.outline || '#ccc',
        text: dbAvatar.name ? dbAvatar.name.substring(0, 2).toUpperCase() : 'ST',
        headshotUrl: dbAvatar.headshotUrl
      };
    }

    const defaultColor = theme.palette.primaryContainer || '#e8f0fe';
    const defaultText = 'ST';
    
    switch (avatarId) {
      case 'avatar_fox':
        return { color: '#ffedd5', border: '#f97316', text: '🦊' };
      case 'avatar_unicorn':
        return { color: '#f3e8ff', border: '#a855f7', text: '🦄' };
      case 'avatar_bear':
        return { color: '#fef3c7', border: '#b45309', text: '🐻' };
      case 'avatar_owl':
        return { color: '#e0f2fe', border: '#0284c7', text: '🦉' };
      case 'avatar_lion':
        return { color: '#fee2e2', border: '#ef4444', text: '🦁' };
      case 'avatar_panda':
        return { color: '#f1f5f9', border: '#475569', text: '🐼' };
      default:
        // Try to match lowercase names if avatarId is like 'fox', 'bear', etc.
        const lowerId = (avatarId || '').toLowerCase();
        if (lowerId.includes('fox')) return { color: '#ffedd5', border: '#f97316', text: '🦊' };
        if (lowerId.includes('unicorn')) return { color: '#f3e8ff', border: '#a855f7', text: '🦄' };
        if (lowerId.includes('bear')) return { color: '#fef3c7', border: '#b45309', text: '🐻' };
        if (lowerId.includes('owl')) return { color: '#e0f2fe', border: '#0284c7', text: '🦉' };
        if (lowerId.includes('lion')) return { color: '#fee2e2', border: '#ef4444', text: '🦁' };
        if (lowerId.includes('panda')) return { color: '#f1f5f9', border: '#475569', text: '🐼' };
        return { color: defaultColor, border: theme.palette.outline, text: defaultText };
    }
  };

  const getObjectiveName = (objectiveId) => {
    const obj = objectives.find(o => o.objectiveId === objectiveId);
    return obj ? obj.name : objectiveId;
  };

  // Color chips maps matching M3 design
  const getGradeChipColor = (level) => {
    if (level === 'KINDERGARTEN' || level === 'ADULT') {
      return {
        backgroundColor: 'rgba(103, 58, 183, 0.1)',
        color: '#673ab7',
        borderColor: 'rgba(103, 58, 183, 0.25)'
      };
    }
    return {
      backgroundColor: theme.palette.secondaryContainer || '#e8f0fe',
      color: theme.palette.onSecondaryContainer || '#1a73e8',
      borderColor: theme.palette.outlineVariant
    };
  };

  const getConfidenceChipColor = (conf) => {
    switch (conf) {
      case 'VERY_LOW':
        return { backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#f44336' };
      case 'LOW':
        return { backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800' };
      case 'MEDIUM':
        return { backgroundColor: 'rgba(33, 150, 243, 0.1)', color: '#2196f3' };
      case 'HIGH':
        return { backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' };
      case 'VERY_HIGH':
        return { backgroundColor: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0' };
      default:
        return {};
    }
  };

  return (
    <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
      
      {isMocked && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: '12px',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          border: `1px solid ${theme.palette.error.main}`,
          color: theme.palette.error.main,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          ⚠️ Demo Mode: Backend API offline. Displaying explicitly mocked student profiles.
        </Box>
      )}

      {/* Search Filter Header Card */}
      <Card sx={{ mb: 4, borderRadius: '16px' }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={3} alignItems="center">
              
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  label="Search students"
                  placeholder="Search by Student Name, Profile ID, or Account ID..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel id="grade-select-label">Grade Level</InputLabel>
                  <Select
                    labelId="grade-select-label"
                    value={grade}
                    label="Grade Level"
                    onChange={(e) => { setGrade(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Grades</MenuItem>
                    <MenuItem value="KINDERGARTEN">Kindergarten</MenuItem>
                    <MenuItem value="ONE">Grade 1</MenuItem>
                    <MenuItem value="TWO">Grade 2</MenuItem>
                    <MenuItem value="THREE">Grade 3</MenuItem>
                    <MenuItem value="FOUR">Grade 4</MenuItem>
                    <MenuItem value="FIVE">Grade 5</MenuItem>
                    <MenuItem value="SIX">Grade 6</MenuItem>
                    <MenuItem value="SEVEN">Grade 7</MenuItem>
                    <MenuItem value="EIGHT">Grade 8</MenuItem>
                    <MenuItem value="NINE">Grade 9</MenuItem>
                    <MenuItem value="TEN">Grade 10</MenuItem>
                    <MenuItem value="ELEVEN">Grade 11</MenuItem>
                    <MenuItem value="TWELVE">Grade 12</MenuItem>
                    <MenuItem value="ADULT">Adult</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3} md={2.5}>
                <FormControl fullWidth>
                  <InputLabel id="confidence-select-label">Reading Confidence</InputLabel>
                  <Select
                    labelId="confidence-select-label"
                    value={confidence}
                    label="Reading Confidence"
                    onChange={(e) => { setConfidence(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Confidence Levels</MenuItem>
                    <MenuItem value="VERY_LOW">Very Low</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="VERY_HIGH">Very High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={2} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="attached-select-label">Binding Status</InputLabel>
                  <Select
                    labelId="attached-select-label"
                    value={attached}
                    label="Binding Status"
                    onChange={(e) => { setAttached(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Binding</MenuItem>
                    <MenuItem value="ATTACHED">Attached Slots</MenuItem>
                    <MenuItem value="NOT_ATTACHED">Unassigned Slots</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={2} md={1}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<FilterIcon />}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  Filter
                </Button>
              </Grid>

            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Grid of Profile Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      ) : profiles.length === 0 ? (
        <Card sx={{ p: 6, borderRadius: '16px', textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            🔍 No reader profiles match your current filter parameters.
          </Typography>
        </Card>
      ) : (
        <Box>
          <Grid container spacing={3}>
            {profiles.map((profile) => {
              const avatar = getAvatarConfig(profile.avatarId);
              return (
                <Grid item xs={12} sm={6} md={4} key={profile.id}>
                  <Card
                    onClick={() => setSelectedProfile(profile)}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      borderRadius: '20px',
                      border: `1px solid ${theme.palette.outlineVariant}`,
                      transition: 'all 0.25s cubic-bezier(0.2, 0, 0, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                      
                      {/* Card Header (Avatar + Name + Binding Badge) */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: avatar.color,
                            border: `2px solid ${avatar.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px',
                            overflow: 'hidden'
                          }}>
                            {avatar.headshotUrl ? (
                              <Box
                                component="img"
                                src={avatar.headshotUrl}
                                alt={profile.name}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              avatar.text
                            )}
                          </Box>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>
                              {profile.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: 'monospace' }}>
                              {profile.id}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Chip
                          label={profile.readerId ? "Attached" : "Unassigned"}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '10px',
                            borderRadius: '6px',
                            backgroundColor: profile.readerId ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)',
                            color: profile.readerId ? theme.palette.success.main : theme.palette.warning.main
                          }}
                        />
                      </Box>

                      {/* Demographic badging */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        <Chip
                          icon={<GradeIcon style={{ fontSize: '13px' }} />}
                          label={profile.gradeLevel.toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, ...getGradeChipColor(profile.gradeLevel) }}
                        />
                        <Chip
                          icon={<ConfidenceIcon style={{ fontSize: '13px' }} />}
                          label={profile.readingConfidence.replace('_', ' ')}
                          size="small"
                          sx={{ fontWeight: 600, ...getConfidenceChipColor(profile.readingConfidence) }}
                        />
                        <Chip
                          label={(profile.nativeLanguage || "ENGLISH").toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '11px', borderRadius: '8px', borderColor: theme.palette.outlineVariant }}
                        />
                      </Box>

                      <Divider sx={{ borderStyle: 'dashed', mb: 2.5 }} />

                      {/* Learning Objective Summary */}
                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CategoryIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                            Objective: <span style={{ color: theme.palette.text.primary, fontWeight: 800 }}>{getObjectiveName(profile.objectiveId)}</span>
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GoalIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                            Goal: <span style={{ color: theme.palette.text.primary }}>{profile.goal.dailyMinutes} mins • {profile.goal.weeklySessions} sessions / week</span>
                          </Typography>
                        </Box>
                      </Box>

                      {/* View Action Indicator */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.primary.main }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                            Inspect Profile
                          </Typography>
                          <ViewIcon fontSize="small" />
                        </Box>
                      </Box>

                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination Controls */}
          {totalCount > size && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Pagination
                count={Math.ceil(totalCount / size)}
                page={page}
                onChange={(e, p) => setPage(p)}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Reader Profile Deep-Dive sidebar drawer */}
      <Drawer
        anchor="right"
        open={!!selectedProfile}
        onClose={() => setSelectedProfile(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 0,
            borderRadius: { xs: 0, sm: '24px 0 0 24px' },
            boxSizing: 'border-box'
          }
        }}
      >
        {selectedProfile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            
            {/* Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 3, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.surfaceContainerHigh
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ReaderIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Student Profile Registry {isMocked && <span style={{ color: theme.palette.error.main, fontSize: '14px' }}>(Mocked)</span>}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedProfile(null)} size="medium">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content Body */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3, '& > *': { flexShrink: 0 } }}>
              
              {/* Profile Card Header Info */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Learning Demographics
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        FULL NAME
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedProfile.name}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        PROFILE ID
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {selectedProfile.id}
                        </Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(selectedProfile.id, "Profile ID")}>
                          <CopyIcon style={{ fontSize: '14px' }} />
                        </IconButton>
                      </Box>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        ACCOUNT ID
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {selectedProfile.accountId}
                        </Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(selectedProfile.accountId, "Account ID")}>
                          <CopyIcon style={{ fontSize: '14px' }} />
                        </IconButton>
                      </Box>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        GRADE LEVEL
                      </Typography>
                      <Chip
                        label={selectedProfile.gradeLevel}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', ...getGradeChipColor(selectedProfile.gradeLevel) }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        READING CONFIDENCE
                      </Typography>
                      <Chip
                        label={selectedProfile.readingConfidence.replace('_', ' ')}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', ...getConfidenceChipColor(selectedProfile.readingConfidence) }}
                      />
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        NATIVE LANGUAGE
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedProfile.nativeLanguage || "ENGLISH"}
                      </Typography>
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

              {/* Engagement & Learning Analytics Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Engagement & Learning Analytics
                  </Typography>
                  {statsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 1.5, color: theme.palette.text.secondary }}>
                        Loading analytics...
                      </Typography>
                    </Box>
                  ) : stats ? (
                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      
                      <ListItem disablePadding sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          backgroundColor: 'rgba(76, 175, 80, 0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#4caf50'
                        }}>
                          <CompletedIcon />
                        </Box>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {stats.srpsCompleted}
                              </Typography>
                              {stats.isMocked && (
                                <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 700, fontSize: '10px' }}>
                                  (Mocked)
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary="Reading Practices Completed"
                          primaryTypographyProps={{ variant: 'h6', sx: { fontWeight: 800 } }}
                          secondaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: theme.palette.text.secondary } }}
                        />
                      </ListItem>

                      <ListItem disablePadding sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          backgroundColor: 'rgba(33, 150, 243, 0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#2196f3'
                        }}>
                          <TimeIcon />
                        </Box>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {formatTimeSpent(stats.totalTimeSeconds)}
                              </Typography>
                              {stats.isMocked && (
                                <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 700, fontSize: '10px' }}>
                                  (Mocked)
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary="Total Time Spent on App"
                          primaryTypographyProps={{ variant: 'h6', sx: { fontWeight: 800 } }}
                          secondaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: theme.palette.text.secondary } }}
                        />
                      </ListItem>

                    </List>
                  ) : (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        ⚠️ Analytics stats are currently unavailable.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Learning Schedule Details Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Reading Objective & Plan
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        OBJECTIVE NAME
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {getObjectiveName(selectedProfile.objectiveId)}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        OBJECTIVE ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {selectedProfile.objectiveId}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        DAILY MINUTES GOAL
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedProfile.goal.dailyMinutes} Minutes / Day
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        WEEKLY SESSIONS GOAL
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedProfile.goal.weeklySessions} Days / Week
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        ONBOARDING STATUS
                      </Typography>
                      <Chip
                        label={selectedProfile.srpIntroStatus.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          backgroundColor: selectedProfile.srpIntroStatus === 'INTRODUCED' ? 'rgba(76, 175, 80, 0.1)' : theme.palette.surfaceContainerLow,
                          color: selectedProfile.srpIntroStatus === 'INTRODUCED' ? theme.palette.success.main : theme.palette.text.secondary
                        }}
                      />
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

              {/* Category Mappings Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Pedagogical Category Interests
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedProfile.interestedCategoryIds.map((catId) => (
                      <Chip
                        key={catId}
                        label={categoryLabels[catId] || catId}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          borderColor: theme.palette.outlineVariant
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* Binding Information Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Student User Binding Parameters
                  </Typography>
                  {selectedProfile.readerId ? (
                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <ListItemText 
                          primary="BOUND READER USER ID" 
                          secondary={selectedProfile.readerId} 
                          primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 800, color: theme.palette.text.secondary } }}
                          secondaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace', fontWeight: 600, color: theme.palette.text.primary, mt: 0.5 } }}
                        />
                        <Button 
                          size="small" 
                          variant="outlined" 
                          startIcon={<CopyIcon style={{ fontSize: '12px' }} />}
                          onClick={() => copyToClipboard(selectedProfile.readerId, "Reader User ID")}
                          sx={{ borderRadius: '8px', textTransform: 'none', py: 0.5 }}
                        >
                          Copy
                        </Button>
                      </ListItem>
                    </List>
                  ) : (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        ⚠️ This slot is currently unbound. A subscriber can link a student reader account to this profile from their subscriber cockpit.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Auditing Fingerprint Card */}
              <Card sx={{ borderRadius: '16px', backgroundColor: theme.palette.surfaceContainerLow }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                    Audit trail & Registry Fingerprint
                  </Typography>
                  <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    
                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        CREATED BY
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {selectedProfile.createdBy}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        CREATED AT
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {formatDate(selectedProfile.createdAt)}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        LAST UPDATED BY
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {selectedProfile.updatedBy || '-'}
                      </Typography>
                    </ListItem>

                    <ListItem disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                        LAST UPDATED AT
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {formatDate(selectedProfile.updatedAt)}
                      </Typography>
                    </ListItem>

                  </List>
                </CardContent>
              </Card>

            </Box>

            {/* Read-Only Footer Context */}
            <Box sx={{ 
              p: 3, 
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.surfaceContainerLow,
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <AdminIcon style={{ fontSize: '16px' }} />
                Administrative Console • Read-Only View Mode
              </Typography>
            </Box>

          </Box>
        )}
      </Drawer>

    </Box>
  );
};

export default ReaderProfileManagementTab;
